import React from "react";
import { Collapse, Row, Col, Card, Button, Alert, Spin } from "antd";

const { Panel } = Collapse;

const ActionsBlock = ({
  isGeneratingActions,
  suggestedActions = {},
  itemEstimation = [],
  applyAction,
  panelHeaderExtra,
  disabled = false,
}) => {
  // total number of suggested actions for this campaign
  const totalActions = itemEstimation.reduce((sum, item) => {
    const actions = suggestedActions[item.offer_item_description] || [];
    return sum + actions.length;
  }, 0);

  return (
    <Collapse defaultActiveKey={["1"]} bordered={false} style={{ marginBottom: 16 }}>
      <Panel
        header={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Actions</span>
            {panelHeaderExtra || (
              <span style={{ fontWeight: 400, fontSize: 14 }}>
                Number of Actions:{" "}
                <span style={{ color: totalActions > 0 ? "#ff4d4f" : "#52c41a", fontWeight: 600 }}>
                  {totalActions}
                </span>
              </span>
            )}
          </div>
        }
        key="1"
      >
        <p style={{ color: "#888", marginBottom: 16 }}>
          Recommended actions to ensure sufficient stock for this campaign
        </p>

        {isGeneratingActions ? (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Generating actions...</p>
          </div>
        ) : (
          <>
            {/* Global warnings */}
            {suggestedActions.global_warning?.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 24 }}
                message={
                  <>
                    {suggestedActions.global_warning.map((w, i) => (
                      <div key={i}>{w.description}</div>
                    ))}
                  </>
                }
              />
            )}

            <Row gutter={[16, 16]}>
              {/* Per-item actions */}
              {itemEstimation.map((item, i) => {
                const actions = suggestedActions[item.offer_item_description] || [];
                if (!actions.length) return null;

                const errors = actions.filter(a => a.severity === "error");
                const warnings = actions.filter(a => a.severity === "warning");
                const normals = actions.filter(a => !a.severity);

                return (
                  <Col xs={24} key={i}>
                    <Card
                      title={item.offer_item_description}
                      size="small"
                      style={{
                        borderLeft: errors.length
                          ? "3px solid #ff4d4f"
                          : warnings.length
                          ? "3px solid #faad14"
                          : "3px solid #52c41a",
                      }}
                      bodyStyle={{ padding: 12 }}
                    >
                      {errors.length > 0 && (
                        <Alert
                          type="error"
                          showIcon
                          style={{ marginBottom: 16 }}
                          message={errors.map((a, idx) => <div key={idx}>{a.description}</div>)}
                        />
                      )}
                      {warnings.length > 0 && (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 16 }}
                          message={warnings.map((a, idx) => <div key={idx}>{a.description}</div>)}
                        />
                      )}

                      <Row gutter={[8, 8]}>
                        {normals.map((action, idx) => {
                          // Determine if action can be applied immediately
                          const canApplyImmediately = ['change_mail_date', 'reduce_mail_qty', 'reduce_mail_qty_print'].includes(action.type);
                          
                          // For combined actions, we can apply the date change but need manual action for ordering
                          const isCombinedAction = action.type === 'combined_date_and_order' || action.type === 'emergency_combined_action';
                          
                          return (
                            <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                              <Card size="small" style={{ padding: 8 }}>
                                <p style={{ fontSize: "0.9rem", marginBottom: 8 }}>
                                  {action.description}
                                </p>
                                {action.newDiff && (
                                  <div style={{ color: "#888", marginBottom: 8 }}>
                                    New difference: <span style={{ color: "#52c41a" }}>{action.newDiff}</span>
                                  </div>
                                )}
                                {canApplyImmediately || isCombinedAction ? (
                                  <>
                                    <Button
                                      type="primary"
                                      block
                                      size="small"
                                      onClick={e => {
                                        e.preventDefault();
                                        applyAction(action, item.offer_item_description);
                                      }}
                                      disabled={disabled}
                                    >
                                      {action.type.includes('date') || isCombinedAction ? 'Apply Date Change' : 
                                       action.type.includes('qty') ? 'Apply Quantity Change' : 'Apply'}
                                    </Button>
                                    
                                    {isCombinedAction && (
                                      <div style={{ textAlign: 'center', color: '#ff4d4f', fontStyle: 'italic', fontSize: '12px', marginTop: '8px' }}>
                                        Also requires ordering {action.orderAmount?.toLocaleString()} units
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div style={{ textAlign: 'center', color: '#ff4d4f', fontStyle: 'italic', fontSize: '12px' }}>
                                    Requires manual action
                                  </div>
                                )}
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </Card>
                  </Col>
                );
              })}

              {/* Print‐stock actions */}
              {suggestedActions.print_stock?.length > 0 && (
                <Col xs={24}>
                  <Card title="Print Stock" size="small" bodyStyle={{ padding: 12 }}>
                    <Row gutter={[8, 8]}>
                      {suggestedActions.print_stock.map((action, idx) => {
                        // Determine if action can be applied immediately
                        const canApplyImmediately = ['reduce_mail_qty_print'].includes(action.type);
                        
                        return (
                          <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                            <Card size="small" style={{ padding: 8 }}>
                              <p style={{ fontSize: "0.9rem", marginBottom: 8 }}>
                                {action.description}
                              </p>
                              {action.newDiff && (
                                <div style={{ color: "#888", marginBottom: 8 }}>
                                  New difference: <span style={{ color: "#52c41a" }}>{action.newDiff}</span>
                                </div>
                              )}
                              {canApplyImmediately ? (
                                <Button
                                  type="primary"
                                  block
                                  size="small"
                                  onClick={e => {
                                    e.preventDefault();
                                    applyAction(action, "print_stock");
                                  }}
                                  disabled={disabled}
                                >
                                  Apply
                                </Button>
                              ) : (
                                <div style={{ textAlign: 'center', color: '#ff4d4f', fontStyle: 'italic', fontSize: '12px' }}>
                                  Requires manual action
                                </div>
                              )}
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card>
                </Col>
              )}
            </Row>

            {/* All‐done banner */}
            {/* {Object.keys(suggestedActions).some(k => k !== "global_warning") &&
              Object.values(suggestedActions)
                .flat()
                .filter(a => !a.severity).length === 0 && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="All recommended actions have been applied. Please review any remaining warnings."
                />
            )} */}
          </>
        )}
      </Panel>
    </Collapse>
  );
};

export default ActionsBlock;
